import { memo, useEffect } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import {
  useHasAclAccess,
  useAnalyticsOrgLocations,
  DASHBOARD_CHART_SIZE,
  DemoChip,
  OverviewInsights,
  appendTime,
  dashboardChartStyle,
} from '@frontend/analytics-shared';
import { Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { PhoneAnalyticsFeatureFlags } from '../feature-flags';
import { useFetchAggregatedCallsSummary } from '../hooks/use-fetch-aggregated-calls-summary';
import { useIsPhoneAnalyticsDemoAccount } from '../hooks/use-is-phone-analytics-demo-account';

type PhoneSummary = {
  answered: number;
  incoming: number;
  total: number;
};

type Props = {
  filters?: AnalyticsCommonTypes.AnalyticsSummaryFilters;
  onLoadData?: (summary: PhoneSummary) => void;
  onLoadingStateChange?: (isLoading?: boolean) => void;
};

const colors = {
  abandoned: theme.colors.warning50,
  answered: theme.colors.success30,
  forwarded: theme.colors.primary20,
  missed: theme.colors.critical30,
};

const labels = {
  abandoned: i18next.t('Abandoned', { ns: 'analytics-phone' }),
  answered: i18next.t('Answered', { ns: 'analytics-phone' }),
  forwarded: i18next.t('Forwarded', { ns: 'analytics-phone' }),
  missed: i18next.t('Missed', { ns: 'analytics-phone' }),
  total: i18next.t('Total', { ns: 'analytics-phone' }),
};

export const PhoneIncomingCategories = memo(({ filters, onLoadData, onLoadingStateChange }: Props) => {
  const { t } = useTranslation('analytics-phone');
  const navigate = useNavigate();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const phoneAnalyticsEnabled = getFeatureFlagValue(PhoneAnalyticsFeatureFlags.featureEnabled);
  const isDemoAccount = useIsPhoneAnalyticsDemoAccount();
  const { noLocationsHaveAclAccess } = useHasAclAccess({
    isDemoAccount,
    permissions: [Permission.ANALYTICS_READ],
  });
  const { isLoadingLocations, preselectedAccessibleLocationIds } = useAnalyticsOrgLocations({
    isAllAccessScopeEnabled: true,
    isDemoAccount,
    module: 'PHONE',
  });

  const { data, isLoading } = useFetchAggregatedCallsSummary({
    end_date: appendTime(filters?.endDate),
    location_id: preselectedAccessibleLocationIds || [],
    start_date: appendTime(filters?.startDate),
    time_zone: filters?.timeZone,
  });

  const incomingCalls = {
    abandoned: data?.answerRate?.abandoned || 0,
    answered: data?.answerRate?.answered || 0,
    missed: data?.answerRate?.missed || 0,
    total: data?.totalCalls?.inbound || 0,
  };

  const forwardedCalls =
    incomingCalls.total - (incomingCalls.abandoned + incomingCalls.answered + incomingCalls.missed);

  useEffect(() => {
    onLoadingStateChange?.(isLoading);
  }, [isLoading]);

  useEffect(() => {
    onLoadData?.({
      answered: data?.answerRate?.answered || 0,
      incoming: data?.totalCalls?.inbound || 0,
      total: data?.totalCalls?.total || 0,
    });
  }, [data]);

  if (!phoneAnalyticsEnabled) {
    return null;
  }

  return (
    <Chart
      bottomContentRenderer={
        <OverviewInsights title={t('Call Answer Rate')} value={`${(data?.answerRate?.rate || 0).toFixed(2)}%`} />
      }
      colors={colors}
      css={dashboardChartStyle}
      commonTooltipLabel={t('Calls')}
      isLoading={isLoading || isLoadingLocations}
      labels={labels}
    >
      <Chart.Header
        actions={[
          {
            label: t('Show More'),
            omit: noLocationsHaveAclAccess,
            onClick: () => {
              navigate({
                to: '/analytics/phone/main',
              });
            },
            trackingId: 'show-more-phone-analytics',
          },
        ]}
        leftElement={isDemoAccount && <DemoChip />}
        title={t('Incoming Phone Calls')}
      />
      <Chart.PieChart
        appearance={{
          width: DASHBOARD_CHART_SIZE,
          height: DASHBOARD_CHART_SIZE,
          innerRadius: 55,
          outerRadius: 95,
        }}
        data={{
          centerMetric: formatters.value(incomingCalls.total),
          centerMetricLabel: labels.total,
          groups: [
            {
              name: 'answered',
              value: incomingCalls.answered,
            },
            {
              name: 'abandoned',
              value: incomingCalls.abandoned,
            },
            {
              name: 'missed',
              value: incomingCalls.missed,
            },
            {
              name: 'forwarded',
              value: forwardedCalls,
            },
          ],
        }}
      />
      {!!incomingCalls.total && (
        <Chart.Legends
          values={{
            answered: formatters.value(incomingCalls.answered),
            forwarded: formatters.value(forwardedCalls),
            missed: formatters.value(incomingCalls.missed),
            abandoned: formatters.value(incomingCalls.abandoned),
          }}
        />
      )}
    </Chart>
  );
});

PhoneIncomingCategories.displayName = 'PhoneIncomingCategories';
